import type {
    PromoLibraryAttributeColumnConfig,
    PromoLibraryColumnConfig,
    PromoLibraryConfig,
    PromoLibraryEditConfig,
    PromoLibraryFieldType,
    PromoLibraryFilterKeyConfig,
    PromoLibraryKpiColumnConfig,
    PromoLibraryKpiFormat,
    PromoLibraryUiConfig,
} from '../promoLibraryConfig';
import type {
    PromoLibraryBackendAttribute,
    PromoLibraryBackendConfig,
    PromoLibraryBackendGroup,
    PromoLibraryBackendKpi,
} from '../schema/promoLibrarySchema';

/**
 * Assembles the runtime `PromoLibraryConfig` from the backend `promo_library`
 * section plus the presentation slice authored in `fe_config`.
 *
 * The backend is the single source of truth for structure — which attributes and
 * KPIs exist, their order, labels, formats, editability, filterability and the
 * grid's paging/sort defaults. It carries nothing visual, so the widths, pinning
 * and muting below are the FE's presentation defaults.
 */

const ATTRIBUTE_COLUMN_WIDTH = 200;
const KPI_COLUMN_WIDTH = 200;

const byOrder = <T extends { order: number }>(a: T, b: T): number => a.order - b.order;

const resolveFormat = (kpi: PromoLibraryBackendKpi): PromoLibraryKpiFormat => {
    if (kpi.data_type === 'boolean' || kpi.data_type === 'text' || kpi.data_type === 'select') return 'text';
    if (kpi.prefix) return 'currency';
    if (kpi.suffix === '%') return 'percent';
    return 'number';
};

const FIELD_TYPE_BY_DATA_TYPE: Record<PromoLibraryBackendKpi['data_type'], PromoLibraryFieldType> = {
    decimal: 'decimal',
    integer: 'integer',
    boolean: 'boolean',
    text: 'text',
    // `select` always renders as a dropdown; the option set comes from `kpi.options`.
    select: 'dropdown',
};

/**
 * Edit metadata for an editable KPI. A fixed option set turns the control into a
 * dropdown regardless of the underlying `data_type`.
 *
 * For `data_type: 'select'`, the option set is taken from `kpi.options[].key`
 * (the value sent to the backend) so the display name can differ from the stored
 * key. For legacy `allowed_values` arrays both are the same string.
 */
const resolveEditConfig = (kpi: PromoLibraryBackendKpi): PromoLibraryEditConfig | undefined => {
    if (!kpi.editable) return undefined;

    const allowedValues =
        kpi.data_type === 'select' && kpi.options?.length
            ? kpi.options.map((opt) => opt.key)
            : (kpi.allowed_values ?? undefined);

    const fieldType: PromoLibraryFieldType = allowedValues?.length
        ? 'dropdown'
        : FIELD_TYPE_BY_DATA_TYPE[kpi.data_type];

    return {
        fieldType,
        minValue: kpi.min_value ?? null,
        maxValue: kpi.max_value ?? null,
        allowNegative: (kpi.min_value ?? 0) < 0,
        allowedDecimals: kpi.data_type === 'integer' ? 0 : (kpi.precision ?? null),
        ...(allowedValues?.length ? { allowedValues } : {}),
        ...(kpi.prefix ? { prefix: kpi.prefix } : {}),
        ...(kpi.suffix ? { suffix: kpi.suffix } : {}),
    };
};

/**
 * @param labelMapKeys – `fieldValueLabelMaps` keys. An attribute whose key matches
 *   one (Danone's `mechanic`) arrives as a raw enum value and is resolved through
 *   that map, so the backend does not have to describe FE label lookups.
 */
const toAttributeColumn = (
    attribute: PromoLibraryBackendAttribute,
    index: number,
    pinnedCount: number,
    labelMapKeys: ReadonlySet<string>,
): PromoLibraryAttributeColumnConfig => ({
    kind: 'attribute',
    key: attribute.key,
    label: attribute.display_name,
    width: ATTRIBUTE_COLUMN_WIDTH,
    ...(index < pinnedCount ? { fixed: 'left' as const } : { muted: true }),
    ...(labelMapKeys.has(attribute.key) ? { resolveLabelFrom: attribute.key } : {}),
});

const toKpiColumn = (kpi: PromoLibraryBackendKpi): PromoLibraryKpiColumnConfig => {
    const format = resolveFormat(kpi);
    const editConfig = resolveEditConfig(kpi);

    return {
        kind: 'kpi',
        key: kpi.key,
        label: kpi.display_name,
        format,
        precision: kpi.data_type === 'integer' ? 0 : kpi.precision,
        width: KPI_COLUMN_WIDTH,
        align: 'left',
        defaultVisible: kpi.default_visible,
        ...(!editConfig ? { muted: true } : {}),
        ...(editConfig ? { editConfig } : {}),
    };
};

const toFilterKey = (
    attribute: PromoLibraryBackendAttribute,
    dimension: string,
    labelMapKeys: ReadonlySet<string>,
): PromoLibraryFilterKeyConfig => ({
    label: attribute.display_name,
    value: attribute.key,
    dimension,
    ...(labelMapKeys.has(attribute.key) ? { resolveLabelFrom: attribute.key } : {}),
});

/**
 * Picks the library the page renders. The backend models `library_groups` as a
 * list to allow several libraries per customer later; until the FE offers a
 * switcher, the first group is the one shown.
 */
const resolveActiveGroup = (backend: PromoLibraryBackendConfig): PromoLibraryBackendGroup | undefined =>
    backend.library_groups[0];

export function adaptPromoLibraryConfig(
    backend: PromoLibraryBackendConfig | undefined,
    ui: PromoLibraryUiConfig | undefined,
    fieldValueLabelMaps: Record<string, Record<string, string>>,
): PromoLibraryConfig | null {
    if (!backend?.enabled || !ui) return null;

    const group = resolveActiveGroup(backend);
    if (!group) return null;

    const labelMapKeys = new Set(Object.keys(fieldValueLabelMaps));

    // `default_visible: false` hides an attribute outright: the toolbar has a KPI
    // picker but no attribute picker, so there is no way to switch one back on.
    const attributes = group.attributes.filter((attribute) => attribute.default_visible).sort(byOrder);
    const kpis = [...group.kpis].sort(byOrder);

    const columns: PromoLibraryColumnConfig[] = [
        ...attributes.map((attribute, index) => toAttributeColumn(attribute, index, group.pinned_attribute_count, labelMapKeys)),
        ...kpis.map(toKpiColumn),
    ];

    if (columns.length === 0) return null;

    // A tenant that authors `filterKeys` owns the panel outright — order, labels
    // and label maps — the way the Calendar and Metric panels are configured. The
    // backend's `filterable` flags are the fallback for tenants that have not.
    const filterKeys = ui.filterKeys?.length
        ? ui.filterKeys
        : attributes.flatMap((attribute) =>
              attribute.filterable && attribute.dimension
                  ? [toFilterKey(attribute, attribute.dimension, labelMapKeys)]
                  : [],
          );

    return {
        ...ui,
        libraryGroup: group.key,
        columns,
        filterKeys,
        showChangeToggle: group.show_change_toggle,
        pageSize: group.page_size,
        defaultSortKey: group.default_sort_key,
    };
}
